import React from 'react';

import styled, { css } from 'styled-components';

import CaretIcon2 from '~/icons/components/CaretIcon2';
import CheckIcon2 from '~/icons/components/CheckIcon2';
import { desktop, mobile } from '~/techstyle-shared/react-components';

import {
  haloRingBase,
  haloRingHover,
  haloRingPressed,
} from '../../styles/haloRing';
import FilterAsset from '../Filters/FilterAssets/FilterAsset';

const CaretIconWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  ${haloRingBase};

  &::before {
    border-radius: 50%;
    inset: -5px;
  }

  ${desktop`
    &:hover::before {
      ${haloRingHover};
    }

    &:active::before {
      ${haloRingPressed};
    }
  `}
`;

const IndicatorRing = styled.div<{ $isSelected: boolean }>`
  position: absolute;
  inset: -1px;
  border-radius: 9px;
  pointer-events: none;
  z-index: 3;
  outline: 1px solid transparent;
  transition: outline-color 0.2s ease;

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      outline-color: ${theme.colors.gridV2DgBorderSelected};
    `}
`;

const FilterCardContainer = styled.div<{ $isSelected: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 220px;
  aspect-ratio: 3 / 4;
  cursor: pointer;

  ${mobile`
    width: 212px;
  `}

  ${({ $isSelected, theme }) =>
    !$isSelected &&
    desktop`
      &:hover ${IndicatorRing} {
        outline-color: ${theme.colors.gridV2DgBorderHover};
      }

      &:active ${IndicatorRing} {
        outline-color: ${theme.colors.gridV2DgBorderPressed};
      }
    `}

  ${({ $isSelected }) =>
    $isSelected &&
    desktop`
      &:hover ${IndicatorRing},
      &:active ${IndicatorRing} {
        outline-width: 2px;
      }
    `}
`;

const FilterCheckboxButton = styled.button<{ $isActive: boolean }>`
  position: absolute;
  top: 0;
  right: 0px;
  z-index: 1;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

const FilterCheckboxVisual = styled.span<{ $isActive: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.colors.black : theme.colors.white};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.black : theme.colors.white};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.2s,
    border-color 0.2s;
`;

const FilterOverlayButton = styled.button<{
  $isTitleOnly: boolean;
  $isExpanded: boolean;
}>`
  position: absolute;
  z-index: 2;
  left: 4px;
  right: 4px;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  padding: ${({ $isExpanded }) =>
    $isExpanded ? '16px' : '12px 16px 12px 16px'};
  cursor: ${({ $isTitleOnly }) => ($isTitleOnly ? 'default' : 'pointer')};
  font-family: inherit;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.gridV2DgOverlayBg};
  backdrop-filter: blur(5px);

  ${({ $isExpanded }) => mobile`
    padding: ${$isExpanded ? '12px' : '8px 12px 10px 12px'};
  `}

  ${desktop`
    &:hover ${CaretIconWrapper}::before {
      ${haloRingHover};
    }

    &:active ${CaretIconWrapper}::before {
      ${haloRingPressed};
    }
  `}
`;

const OverlayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 14px;

  ${mobile`
    font-size: 12px;
  `}
`;

const OverlayTitle = styled.p<{ $isSelected: boolean }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  flex: 1 0 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.gridV2DgLabel};
  text-overflow: ellipsis;
  font-size: 12px;
  font-style: normal;
  line-height: ${16 / 12};
  font-weight: ${({ $isSelected }) => ($isSelected ? 700 : 600)};
  margin: 0;

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: auto;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
    `}
`;

const StyledCaretIcon2 = styled(CaretIcon2).attrs(({ theme }) => ({
  color: theme.colors.gridV2DgLabel,
}))<{ $isExpanded: boolean }>`
  transform: ${({ $isExpanded }) =>
    $isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: transform 200ms ease;
`;

const ExpandableContent = styled.div<{ $isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? '1fr' : '0fr')};
  transition: grid-template-rows 150ms ease-in-out;
  padding-top: ${({ $isExpanded }) => ($isExpanded ? '4px' : '0px')};

  > div {
    overflow: hidden;
  }
`;

const OverlayDescription = styled.p`
  margin-bottom: 0;
  font-size: 12px;
  line-height: ${16 / 12};
  font-weight: 350;
  color: ${({ theme }) => theme.colors.gridV2NumResultsText};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OverlayFeatures = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: ${({ $compact }) => ($compact ? '16px' : '20px')};
`;

const FeatureRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
  align-self: stretch;
  font-family: inherit;
`;

const FeatureKey = styled.p`
  color: ${({ theme }) => theme.colors.gridV2DgFeatureKeyText};
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: ${12 / 10};
`;

const FeatureValue = styled.p`
  color: ${({ theme }) => theme.colors.gridV2DgFeatureValueText};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: stretch;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  font-style: normal;
  font-weight: 350;
  line-height: ${16 / 12};
`;

type DiscoveryGuideCardProps = {
  asset?: Record<string, any>;
  description?: string;
  features?: Array<{ key: string; value: string }>;
  isExpanded: boolean;
  isFetchingAssets: boolean;
  isPlayingAllVideos: boolean;
  isSelected: boolean;
  numFeatures?: number;
  playbackEpochMs: number;
  onToggleExpanded: () => void;
  onToggleFilter: () => void;
  showDescription?: boolean;
  showTitleOnly?: boolean;
  title?: string;
  value: string;
};

const DiscoveryGuideCard = ({
  asset,
  description,
  features,
  isExpanded,
  isFetchingAssets,
  isPlayingAllVideos,
  isSelected,
  numFeatures,
  playbackEpochMs,
  onToggleExpanded,
  onToggleFilter,
  showDescription,
  showTitleOnly,
  title,
  value,
}: DiscoveryGuideCardProps) => {
  const hasExpandableContent =
    (showDescription && description) || !!features?.length;
  const isTitleOnly = showTitleOnly || !hasExpandableContent;
  const displayTitle = title || value;

  return (
    <FilterCardContainer $isSelected={isSelected} onClick={onToggleFilter}>
      <FilterCheckboxButton
        type="button"
        $isActive={isSelected}
        aria-pressed={isSelected}
        aria-label={`${isSelected ? 'Remove' : 'Apply'} ${displayTitle} filter`}
        onClick={(event) => {
          event.stopPropagation();
          onToggleFilter();
        }}
      >
        <FilterCheckboxVisual $isActive={isSelected}>
          {isSelected && <CheckIcon2 width={8} height={6} />}
        </FilterCheckboxVisual>
      </FilterCheckboxButton>
      <FilterAsset
        imageSrc={asset?.imageFilename}
        alt={asset?.label || displayTitle}
        isFetchingAssets={isFetchingAssets}
        isPlaying={isPlayingAllVideos}
        syncEpochMs={playbackEpochMs}
      />
      <IndicatorRing $isSelected={isSelected} />
      <FilterOverlayButton
        type="button"
        $isTitleOnly={isTitleOnly}
        $isExpanded={isExpanded}
        onClick={(event) => {
          event.stopPropagation();
          if (!isTitleOnly) {
            onToggleExpanded();
          }
        }}
        aria-expanded={isTitleOnly ? undefined : isExpanded}
      >
        <OverlayHeader>
          <OverlayTitle $isSelected={isSelected}>{displayTitle}</OverlayTitle>
          {!isTitleOnly && (
            <CaretIconWrapper>
              <StyledCaretIcon2 aria-hidden="true" $isExpanded={isExpanded} />
            </CaretIconWrapper>
          )}
        </OverlayHeader>
        {!isTitleOnly && (
          <ExpandableContent $isExpanded={isExpanded}>
            <div>
              {showDescription && (
                <OverlayDescription>{description}</OverlayDescription>
              )}
              {features?.length > 0 && (
                <OverlayFeatures $compact={!showDescription}>
                  {features
                    ?.slice(0, numFeatures)
                    ?.map(({ key, value: featureValue }) => (
                      <FeatureRow key={`${featureValue}-${key}`}>
                        <FeatureKey>{key}</FeatureKey>
                        <FeatureValue>{featureValue}</FeatureValue>
                      </FeatureRow>
                    ))}
                </OverlayFeatures>
              )}
            </div>
          </ExpandableContent>
        )}
      </FilterOverlayButton>
    </FilterCardContainer>
  );
};

export default DiscoveryGuideCard;
